import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { LogIn } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useToast } from '../../hooks/use-toast';
import { useAdmin } from '../../context/AdminContext';
import { useRecaptcha } from '../../hooks/useRecaptcha';

const LOGO_URL = '/logo.png';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login } = useAdmin();
  const { execute: executeRecaptcha, enabled: recaptchaEnabled } = useRecaptcha();
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const recaptchaToken = await executeRecaptcha('admin_login');
    const success = await login(credentials.username, credentials.password, recaptchaToken);
    if (success) {
      toast({ title: "Login Successful", description: "Welcome to admin panel!" });
      navigate('/admin/dashboard');
    } else {
      toast({ title: "Login Failed", description: "Invalid username or password", variant: "destructive" });
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-gray-100">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mb-4 flex justify-center">
            <img src={LOGO_URL} alt="iTRONiX" className="h-16 w-auto" />
          </div>
          <CardTitle className="text-2xl">Admin Login</CardTitle>
          <p className="text-gray-600 mt-2">Itronix Control Panel</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Username</label>
              <Input type="text" value={credentials.username} onChange={(e) => setCredentials({ ...credentials, username: e.target.value })} placeholder="admin" required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <Input type="password" value={credentials.password} onChange={(e) => setCredentials({ ...credentials, password: e.target.value })} placeholder="••••••••" required />
            </div>
            <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={loading}>
              <LogIn className="mr-2 h-4 w-4" />
              {loading ? 'Logging in...' : 'Login'}
            </Button>
            {recaptchaEnabled && (
              <p className="text-xs text-gray-400 text-center" data-testid="recaptcha-notice">
                Protected by Google <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">reCAPTCHA</a>
              </p>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
